import { useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Box, Glasses, Monitor, Tablet } from "lucide-react";
import type { Manual } from "@arc/contracts";
import { Button } from "@/components/ui/button";

export type ManualPlatforms = Pick<Manual, "is2dWebManual" | "is3dWebManual" | "isVrManual" | "isArManual">;
type PlatformKey = keyof ManualPlatforms;

const GROUPS = [
  {
    titleKey: "manualManagement.platforms.groupWeb",
    items: [
      { key: "is2dWebManual", textKey: "manualManagement.platforms.platform2d", icon: Monitor },
      { key: "is3dWebManual", textKey: "manualManagement.platforms.platform3d", icon: Box },
    ],
  },
  {
    titleKey: "manualManagement.platforms.groupXr",
    items: [
      { key: "isVrManual", textKey: "manualManagement.platforms.platformVr", icon: Glasses },
      { key: "isArManual", textKey: "manualManagement.platforms.platformAr", icon: Tablet },
    ],
  },
] as const satisfies ReadonlyArray<{ titleKey: string; items: ReadonlyArray<{ key: PlatformKey; textKey: string; icon: ComponentType<{ className?: string; "aria-hidden"?: "true" }> }> }>;

/**
 * Where a manual is usable (ADR-057, legacy `CreatePlatformSelector`): Web [2D | 3D] and XR-Brille / Tablet [VR | AR]. At least one
 * stays active, so the last one can't be switched off and says why. Like the legacy, the only other note is that a manual without
 * a web platform can't be viewed on the web. Without `onChange` it only shows the state, with no notes.
 */
export function PlatformSelector({
  value,
  onChange,
  isDisabled = false,
}: {
  value: ManualPlatforms;
  onChange?: (next: ManualPlatforms) => void;
  isDisabled?: boolean;
}) {
  const { t } = useTranslation();
  const [minOneWarning, setMinOneWarning] = useState(false);
  const readOnly = !onChange;
  const noteKey = minOneWarning
    ? "manualManagement.platforms.minOne"
    : !value.is2dWebManual && !value.is3dWebManual
      ? "manualManagement.platforms.hintNotInWeb"
      : null;

  const toggle = (key: PlatformKey) => {
    const activeCount = [value.is2dWebManual, value.is3dWebManual, value.isVrManual, value.isArManual].filter(Boolean).length;
    if (value[key] && activeCount === 1) {
      setMinOneWarning(true);
      return;
    }
    setMinOneWarning(false);
    onChange?.({ ...value, [key]: !value[key] });
  };

  return (
    <div className="flex min-w-0 flex-col">
      {/* Two equal columns over the full width, the same grid as the drawer's View/Edit buttons. */}
      <div className="grid grid-cols-2 gap-2">
        {GROUPS.map((group) => (
          <div key={group.titleKey} className="flex min-w-0 flex-col gap-1">
            <span className="text-muted-foreground text-xs">{t(group.titleKey)}</span>
            <div role="group" aria-label={t(group.titleKey)} className="border-border flex items-center gap-0.5 rounded-md border p-0.5">
              {group.items.map(({ key, textKey, icon: Icon }) =>
                readOnly ? (
                  <span
                    key={key}
                    className={`inline-flex h-7 flex-1 items-center justify-center gap-1 rounded-md px-2.5 text-[0.8rem] font-medium ${value[key] ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                  >
                    <Icon aria-hidden="true" className="size-3.5" />
                    {t(textKey)}
                    <span className="sr-only">{t(value[key] ? "manualManagement.platforms.on" : "manualManagement.platforms.off")}</span>
                  </span>
                ) : (
                  <Button key={key} variant={value[key] ? "default" : "ghost"} size="sm" className="flex-1" aria-pressed={value[key]} isDisabled={isDisabled} onPress={() => toggle(key)}>
                    <Icon aria-hidden="true" className="size-3.5" />
                    {t(textKey)}
                  </Button>
                ),
              )}
            </div>
          </div>
        ))}
      </div>
      {readOnly ? null : (
        // The live region stays mounted so a note appearing is announced; the margin sits on the note, so no note takes no space.
        <div aria-live="polite" className={`m-0 flex flex-col text-xs ${minOneWarning ? "text-destructive" : "text-muted-foreground"}`}>
          {noteKey ? <span className="mt-2">{t(noteKey)}</span> : null}
        </div>
      )}
    </div>
  );
}
